const capabilities = [
  {
    title: 'iOS Platform Expertise',
    description:
      'Deep specialization in designing and developing modern digital solutions for the iOS platform, leveraging the latest engineering standards and platform capabilities to deliver robust, high-quality systems.',
    layout: 'top-text',
  },
  {
    title: 'System Reliability',
    description:
      'We prioritize stability and long-term maintainability in every project. Our approach emphasizes thorough testing, clean dependency management, and defensive coding practices that keep systems running smoothly.',
    layout: 'bottom-text',
  },
  {
    title: 'Technical Excellence',
    description:
      'Continuous improvement of development workflows and adoption of up-to-date engineering standards ensure every deliverable meets the highest bar for code quality, documentation, and architectural integrity.',
    layout: 'bottom-text',
  },
];

function CapabilityCard({
  title,
  description,
  layout,
}: {
  title: string;
  description: string;
  layout: string;
}) {
  return (
    <div className="bg-background-100/70 rounded-2xl p-6 md:p-8 flex flex-col h-full">
      {layout === 'top-text' ? (
        <>
          <h3 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 leading-snug">
            {title}
          </h3>
          <div className="flex-1 mt-5">
            <div className="w-full aspect-[4/3] rounded-xl bg-background-200/80 relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-primary-100/30 via-transparent to-accent-100/20" />
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-2/3 h-px bg-background-300/60" />
              <div className="absolute top-1/3 left-1/3 w-1/3 h-px bg-background-300/40" />
              <div className="absolute bottom-1/3 right-1/3 w-1/4 h-px bg-background-300/40" />
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-px h-1/3 bg-background-300/60" />
            </div>
          </div>
          <p className="mt-5 text-foreground-600 text-sm md:text-base leading-relaxed font-body">
            {description}
          </p>
        </>
      ) : (
        <>
          <h3 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 text-center leading-snug">
            {title}
          </h3>
          <div className="flex-1 mt-5">
            <div className="w-full aspect-[4/3] rounded-xl bg-background-200/80 relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-secondary-100/30 via-transparent to-primary-100/20" />
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-16 h-16 rounded-full border border-background-300/50" />
                <div className="absolute w-24 h-24 rounded-full border border-background-300/30" />
              </div>
            </div>
          </div>
          <p className="mt-5 text-foreground-600 text-sm md:text-base leading-relaxed text-center font-body">
            {description}
          </p>
        </>
      )}
    </div>
  );
}

export default function CapabilitiesSection() {
  return (
    <section
      className="relative w-full bg-background-100/60 py-20 md:py-28 px-6 md:px-12 lg:px-20"
      aria-label="Engineering capabilities"
    >
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-col lg:flex-row lg:items-start gap-4 lg:gap-16 mb-16 md:mb-20">
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-950 leading-tight flex-shrink-0">
            How We Work
          </h2>
          <p className="text-foreground-600 text-base md:text-lg leading-relaxed max-w-lg font-body lg:pt-2">
            Our engineering methodology combines disciplined process with
            creative problem-solving to deliver exceptional results.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          <div className="md:col-span-1">
            <CapabilityCard
              title={capabilities[0].title}
              description={capabilities[0].description}
              layout={capabilities[0].layout}
            />
          </div>
          <div className="md:col-span-1">
            <CapabilityCard
              title={capabilities[1].title}
              description={capabilities[1].description}
              layout={capabilities[1].layout}
            />
          </div>
          <div className="md:col-span-1">
            <CapabilityCard
              title={capabilities[2].title}
              description={capabilities[2].description}
              layout={capabilities[2].layout}
            />
          </div>
        </div>
      </div>
    </section>
  );
}